import { format } from 'date-fns';
import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLConnection } from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { CompanyPerformance } from './useCompanyPerformanceGraphQL';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- Types ---

export interface DailyTrendPoint {
  day: string;
  snapshotDate: string;
  dso: number;
  cei: number;
}

export interface MultiCompanyDetailResult {
  company: CompanyPerformance | null;
  trendPoints: DailyTrendPoint[];
}

// --- GraphQL queries (split into separate requests to avoid concurrent DbContext access) ---

interface CompanyPerformanceNode {
  companyId: number;
  companyName: string | null;
  companyCode: string | null;
  currencyCode: string | null;
  totalAR: number;
  totalOverdue: number;
  overduePercent: number;
  avgDso: number;
  cei: number;
  avgDaysToPay: number;
  customerCount: number;
}

interface MultiCompanyDailyTrendNode {
  companyId: number;
  snapshotDate: string;
  dso: number;
  cei: number;
}

interface CompanyPerformanceResponse {
  multiCompanyDashboard: {
    companyPerformanceList: GraphQLConnection<CompanyPerformanceNode>;
  };
}

interface DailyTrendResponse {
  multiCompanyDashboard: {
    multiCompanyDailyTrendList: GraphQLConnection<MultiCompanyDailyTrendNode>;
  };
}

const COMPANY_PERFORMANCE_QUERY = `
  query CompanyPerformanceDetail(
    $first: Int,
    $filter: CompanyPerformanceComparisonFilterInput
  ) {
    multiCompanyDashboard {
      companyPerformanceList(first: $first, filter: $filter) {
        edges {
          node {
            companyId
            companyName
            companyCode
            currencyCode
            totalAR
            totalOverdue
            overduePercent
            avgDso
            cei
            avgDaysToPay
            customerCount
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

const DAILY_TREND_QUERY = `
  query MultiCompanyDailyTrend(
    $first: Int,
    $filter: MultiCompanyDailyTrendFilterInput
  ) {
    multiCompanyDashboard {
      multiCompanyDailyTrendList(first: $first, filter: $filter) {
        edges {
          node {
            companyId
            snapshotDate
            dso
            cei
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Mock data ---

function generateMockDetail(companyId: number): MultiCompanyDetailResult {
  const mockCompanies: CompanyPerformance[] = [
    {
      id: '1',
      name: 'Acme Corporation',
      companyCode: null,
      currency: 'GBP',
      totalAR: 450000,
      overdue: 125000,
      overduePercent: 27.8,
      dso: 45,
      cei: 87,
      avgDaysToPay: 42,
      customerCount: 0,
    },
    {
      id: '2',
      name: 'Global Industries Ltd',
      companyCode: null,
      currency: 'EUR',
      totalAR: 680000,
      overdue: 95000,
      overduePercent: 14.0,
      dso: 38,
      cei: 92,
      avgDaysToPay: 35,
      customerCount: 0,
    },
    {
      id: '3',
      name: 'Tech Innovations Inc',
      companyCode: null,
      currency: 'USD',
      totalAR: 320000,
      overdue: 180000,
      overduePercent: 56.3,
      dso: 72,
      cei: 65,
      avgDaysToPay: 68,
      customerCount: 0,
    },
    {
      id: '4',
      name: 'Retail Solutions Co',
      companyCode: null,
      currency: 'GBP',
      totalAR: 540000,
      overdue: 75000,
      overduePercent: 13.9,
      dso: 32,
      cei: 94,
      avgDaysToPay: 30,
      customerCount: 0,
    },
  ];

  const company = mockCompanies.find((x) => x.id === String(companyId)) ?? null;

  const mockTrend: DailyTrendPoint[] = [
    { day: 'Day 1', snapshotDate: '2026-03-22', dso: 48, cei: 85 },
    { day: 'Day 5', snapshotDate: '2026-03-26', dso: 50, cei: 83 },
    { day: 'Day 10', snapshotDate: '2026-03-31', dso: 52, cei: 81 },
    { day: 'Day 15', snapshotDate: '2026-04-05', dso: 49, cei: 84 },
    { day: 'Day 20', snapshotDate: '2026-04-10', dso: 47, cei: 86 },
    { day: 'Day 25', snapshotDate: '2026-04-15', dso: 45, cei: 88 },
    { day: 'Day 30', snapshotDate: '2026-04-20', dso: 45, cei: 87 },
  ];

  return { company, trendPoints: mockTrend };
}

// --- Hook ---

export function useMultiCompanyDetailGraphQL(companyId: number) {
  const apiConfig = useApiConfig();

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const mockResult = generateMockDetail(companyId);

  return useModeAwareQuery<MultiCompanyDetailResult, Error>(
    ['multi-company-detail', companyId],
    async ({ signal }) => {
      const companyFilter = {
        companyId: { eq: companyId },
      };

      const trendFilter = {
        companyId: { eq: companyId },
        snapshotDate: {
          ge: thirtyDaysAgo.toISOString().split('T')[0],
        },
      };

      // Two separate HTTP requests so each gets its own scoped DbContext
      const [perfResponse, trendResponse] = await Promise.all([
        graphqlQuery<CompanyPerformanceResponse>(
          apiConfig,
          COMPANY_PERFORMANCE_QUERY,
          { first: 1, filter: companyFilter },
          signal,
        ),
        graphqlQuery<DailyTrendResponse>(apiConfig, DAILY_TREND_QUERY, { first: 100, filter: trendFilter }, signal),
      ]);

      const summaryNodes = perfResponse.multiCompanyDashboard.companyPerformanceList.edges.map((x) => x.node);
      const trendNodes = trendResponse.multiCompanyDashboard.multiCompanyDailyTrendList.edges.map((x) => x.node);

      const companyNode = summaryNodes[0] ?? null;
      const company: CompanyPerformance | null = companyNode
        ? {
            id: String(companyNode.companyId),
            name: companyNode.companyName ?? '',
            companyCode: companyNode.companyCode,
            currency: companyNode.currencyCode ?? '',
            totalAR: companyNode.totalAR,
            overdue: companyNode.totalOverdue,
            overduePercent: companyNode.overduePercent,
            dso: companyNode.avgDso,
            cei: companyNode.cei,
            avgDaysToPay: companyNode.avgDaysToPay,
            customerCount: companyNode.customerCount,
          }
        : null;

      trendNodes.sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate));

      const trendPoints: DailyTrendPoint[] = trendNodes.map((x) => ({
        day: format(new Date(x.snapshotDate), 'MMM d'),
        snapshotDate: x.snapshotDate,
        dso: x.dso,
        cei: x.cei,
      }));

      return { company, trendPoints };
    },
    mockResult,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
      enabled: companyId > 0,
    },
  );
}
